import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties } from 'react';
import styled from 'styled-components';

import type { Skin } from '@sorare/core/src/__generated__/globalTypes.ts';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport/index.ts';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { sortBy } from '@sorare/core/src/lib/arrays';

import { CollectionAnimationClub } from '#src/components/commonCard/RevealPackCardsDialog/CollectionAnimation/CollectionAnimationClub/index.tsx';
import { CollectionAnimationHeader } from '#src/components/commonCard/RevealPackCardsDialog/CollectionAnimation/CollectionAnimationHeader/index.tsx';
import { collectionAnimationConfig } from '#src/components/commonCard/RevealPackCardsDialog/CollectionAnimation/animationConfig.ts';

import type {
  CollectionAnimationCompetitionQuery,
  CollectionAnimationCompetitionQueryVariables,
  CollectionAnimationCompetition_anyCardInterface,
  CollectionAnimationCompetition_competition,
} from './__generated__/index.graphql.ts';
import { useAnimatingCardIndex } from './useAnimatingCardIndex.ts';
import { useDisplayedCollections } from './useDisplayedCollections.ts';
import { useTranslateY } from './useTranslateY.ts';

const Root = styled(Vertical)`
  width: 100%;
  min-width: 100%;
  background-color: var(--c-black);
  position: relative;
  height: 100%;
`;

const CollectionsWrapper = styled(Vertical)`
  transition: transform ${collectionAnimationConfig.scrollDurationMs}ms
    var(--ease-out-slide);
  flex: 1;
`;

const COLLECTION_ANIMATION_COMPETITION_QUERY = gql`
  query CollectionAnimationCompetitionQuery(
    $user: String!
    $competitionSlug: String!
    $cardSetSlug: String!
    $sport: Sport!
    $skins: [Skin!]
  ) {
    currentUser {
      slug
      competitionCardCollections(
        competitionSlugs: [$competitionSlug]
        sport: $sport
        cardSetSlug: $cardSetSlug
      ) {
        cardCollections(first: 50) {
          slug
          userCardCollection(forUserSlug: $user) {
            id
            slots(skins: $skins) {
              slug
              highlightedCardCollectionCard {
                id
                anyCard {
                  slug
                }
              }
            }
          }
          anyTeam {
            slug
            shortName
          }
          ...CollectionAnimationHeader_cardCollection
          ...CollectionAnimationClub_cardCollection
        }
      }
    }
  }
  ${CollectionAnimationHeader.fragments.cardCollection}
  ${CollectionAnimationClub.fragments.cardCollection}
` as TypedDocumentNode<
  CollectionAnimationCompetitionQuery,
  CollectionAnimationCompetitionQueryVariables
>;

type Props = {
  competition: CollectionAnimationCompetition_competition;
  newCards: CollectionAnimationCompetition_anyCardInterface[];
  shouldAnimate: boolean;
  onAnimationEnd: () => void;
  cardSetSlug: string;
  newCardSkins: Skin[];
};

export const CollectionAnimationCompetition = ({
  competition,
  newCards,
  shouldAnimate,
  onAnimationEnd,
  cardSetSlug,
  newCardSkins,
}: Props) => {
  const { currentUser: contextCurrentUser } = useCurrentUserContext();
  const { sport } = useSportContext();
  const { data, loading } = useQuery(COLLECTION_ANIMATION_COMPETITION_QUERY, {
    variables: {
      user: contextCurrentUser?.slug || '',
      competitionSlug: competition.slug,
      cardSetSlug,
      sport,
      skins: newCardSkins,
    },
    skip: !contextCurrentUser?.slug,
  });
  const cardCollections =
    data?.currentUser?.competitionCardCollections?.at(0)?.cardCollections || [];

  const displayedCardCollections = useDisplayedCollections({
    cardCollections,
    newCards,
  });
  const sortedNewCards = sortBy(
    card => card.anyTeam?.shortName || '',
    [...newCards]
  );
  const { translateY, scrollTo, clubRefCallback } = useTranslateY({
    sortedNewCards,
    displayedCardCollections,
  });
  const {
    animatingCardIndex,
    setAnimatingCardIndex,
    setAnimatingCardIndexAfterScroll,
  } = useAnimatingCardIndex({ shouldAnimate: shouldAnimate && !loading });

  if (loading || displayedCardCollections.length === 0) {
    return null;
  }

  return (
    <Root>
      <CollectionAnimationHeader
        cardCollections={cardCollections}
        competition={competition}
        newCardSlugs={sortedNewCards.map(card => card.slug)}
        animatingCardIndex={animatingCardIndex}
        cardSetSlug={cardSetSlug}
      />
      <CollectionsWrapper
        style={
          {
            transform: `translateY(${translateY}px)`,
          } as CSSProperties
        }
      >
        {displayedCardCollections.map((cardCollection, index) => (
          <CollectionAnimationClub
            key={cardCollection.slug}
            ref={clubRefCallback}
            index={index}
            cardCollection={cardCollection}
            newCards={sortedNewCards}
            animatingCardIndex={animatingCardIndex}
            onCardAnimationEnd={() => {
              if (animatingCardIndex === undefined) {
                return;
              }
              if (animatingCardIndex === sortedNewCards.length - 1) {
                onAnimationEnd();
                return;
              }
              const isNextCardInSameCollection =
                sortedNewCards.at(animatingCardIndex + 1)?.anyTeam.slug ===
                sortedNewCards.at(animatingCardIndex)?.anyTeam.slug;

              if (!isNextCardInSameCollection) {
                scrollTo(animatingCardIndex + 1);
                setAnimatingCardIndexAfterScroll(animatingCardIndex + 1);
              } else {
                setAnimatingCardIndex(animatingCardIndex + 1);
              }
            }}
          />
        ))}
      </CollectionsWrapper>
    </Root>
  );
};

CollectionAnimationCompetition.fragments = {
  anyCardInterface: gql`
    fragment CollectionAnimationCompetition_anyCardInterface on AnyCardInterface {
      slug
      anyTeam {
        slug
        shortName
        ... on Club {
          slug
          domesticLeague {
            slug
          }
        }
      }
    }
    ${CollectionAnimationHeader.fragments.competition}
  ` as TypedDocumentNode<CollectionAnimationCompetition_anyCardInterface>,
  competition: gql`
    fragment CollectionAnimationCompetition_competition on Competition {
      slug
      ...CollectionAnimationHeader_competition
    }
    ${CollectionAnimationHeader.fragments.competition}
  ` as TypedDocumentNode<CollectionAnimationCompetition_competition>,
};
